import { RESEND_REAUTH_CODE_MUTATION, VALIDATE_REAUTH_CODE_MUTATION } from '@graphql';
import { graphqlApi, ReauthChallengeErrorCode, reauthChallengeErrorHandler } from '@utils';
import { flow, makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { DEFAULT_HTTP_RESPONSE } from './defaults';

export class ReauthChallengeStore {
  validateReauthCodeResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  resendReauthCodeResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  private otpCode: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  clearReauthToken = () => {
    this.otpCode = null;
  };

  setOtpCode = (token?: string) => {
    if (!token) return;
    this.otpCode = token;
  };

  getOtpCode = () => this.otpCode;

  /**
   * When a mutation returns STEP_UP_REQUIRED, persist optional session token for `validateReauthCode`.
   */
  handleReauthStepUpRequired(response: {
    data?: { errors?: Array<{ extensions?: { otpCode?: string } }> };
  }): ReauthChallengeErrorCode {
    const otpCode = response?.data?.errors?.[0]?.extensions?.otpCode;
    this.setOtpCode(otpCode);
    toast('Reauth challenge required', { type: 'warning' });
    return ReauthChallengeErrorCode.StepUpRequired;
  }

  validateReauthCode = flow(function* validateReauthCode(
    this: ReauthChallengeStore,
    otpCode: string | null,
  ): any {
    try {
      this.validateReauthCodeResponse.status = 'pending';
      const response = yield graphqlApi(VALIDATE_REAUTH_CODE_MUTATION, { otpCode });
      const errorCode = reauthChallengeErrorHandler(response);
      if (errorCode === ReauthChallengeErrorCode.InvalidCode) {
        toast('Invalid code', { type: 'warning' });
        this.validateReauthCodeResponse.status = 'badRequest';
        return false;
      }

      if (errorCode === ReauthChallengeErrorCode.TooManyAttempts) {
        toast('Too many attempts try again later', { type: 'error' });
        this.validateReauthCodeResponse.status = 'badRequest';
        return false;
      }

      if (errorCode === ReauthChallengeErrorCode.CodeNotFound) {
        toast('Code not found request a new code', { type: 'error' });
        this.validateReauthCodeResponse.status = 'badRequest';
        return false;
      }

      if (errorCode === ReauthChallengeErrorCode.CodeExpired) {
        toast('Code expired request a new code', { type: 'error' });
        this.validateReauthCodeResponse.status = 'badRequest';
        return false;
      }

      this.validateReauthCodeResponse.status = 'ok';
      this.otpCode = null;
      return true;
    } catch (err: any) {
      this.validateReauthCodeResponse.status = 'badRequest';
      return false;
    }
  }).bind(this);

  resendReauthCode = flow(function* resendReauthCode(this: ReauthChallengeStore): any {
    try {
      this.resendReauthCodeResponse.status = 'pending';
      const response = yield graphqlApi(RESEND_REAUTH_CODE_MUTATION);
      const errorCode = reauthChallengeErrorHandler(response);
      if (errorCode === ReauthChallengeErrorCode.CodeNotFound) {
        toast('Code not found request a new code', { type: 'error' });
        this.resendReauthCodeResponse.status = 'badRequest';
        return false;
      }

      if (errorCode === ReauthChallengeErrorCode.WaitToSendCode) {
        toast('Wait to send code try again later', { type: 'warning' });
        this.resendReauthCodeResponse.status = 'badRequest';
        return false;
      }

      const payload = response?.data?.data?.resendReauthCode;

      if (payload?.success === true) {
        this.resendReauthCodeResponse.status = 'ok';
        this.setOtpCode(payload?.otpCode);
      }
      return true;
    } catch (err: any) {
      this.resendReauthCodeResponse.status = 'badRequest';
      return false;
    }
  }).bind(this);
}
